<template>
  <router-view />
  <AppUpdateDialog />
  <DialogsWrapper />
  <!-- Test Venue preview mode (superdemo venues only) — see composables/usePreviewMode.ts -->
  <PreviewModeGuard />
  <!-- Animated Lottie boot splash (native launch experience only). -->
  <AppSplash v-if="showBootSplash" @done="showBootSplash = false" />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import { DialogsWrapper } from 'vuejs-confirm-dialog'
import { Capacitor } from '@capacitor/core'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import AppUpdateDialog from './components/AppUpdateDialog.vue'
import AppSplash from './components/AppSplash.vue'
import PreviewModeGuard from './components/demo/PreviewModeGuard.vue'
import usePlatform from './composables/usePlatform'
import { useAppUpdate } from './composables/useAppUpdate'
import useAuth, { initUser, refreshUser } from './composables/useAuth'
import usePushNotifications from './composables/usePushNotifications'
import { useLanguage } from './services/language'
import { localeFromServerIndex } from './i18n/supported-locales'
import { IS_GUEST_APP } from './utils/appMode'

const router = useRouter()
const { checkForUpdates } = useAppUpdate()
const { refreshTokenIfEnabled } = usePushNotifications()
const $q = useQuasar()
const { t } = useI18n({ useScope: 'global' })
const APP_VERSION = process.env.APP_VERSION

// Animated boot splash: native only (web keeps the lightweight #app-loading loader).
const { isCapacitor } = usePlatform()
const showBootSplash = ref(isCapacitor)

// The app interface obeys the logged-in user's stamped preferred language
// (User.language) — applied on login/session-restore and whenever it changes
// (e.g. edit profile). The top-bar picker can still override within a session.
const { userProps } = useAuth()
const { langOptions, setLocale } = useLanguage()
watch(() => userProps.language, (idx) => {
  const code = localeFromServerIndex(idx as number)
  const opt = langOptions.value.find((l) => l.isoName === code)
    || langOptions.value.find((l) => l.isoName.split('-')[0] === code.split('-')[0])
  if (opt) void setLocale(opt)
}, { immediate: true })


let versionCheckInterval: ReturnType<typeof setInterval> | undefined

function reloadAndClearCache() {
  window.location.reload()
}

async function checkForNewVersion() {
  if (Capacitor.getPlatform() !== 'web') return

  try {
    // Cache-busting query + no-store so we always read the freshly deployed file,
    // not a cached copy. version.json is emitted at build time (see quasar.config.js).
    const response = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
    })
    if (!response.ok) return

    const data = (await response.json()) as { version?: string }
    const serverVersion = data?.version

    if (serverVersion && serverVersion !== APP_VERSION) {
      $q.notify({
        message: t('versionCheck.newVersionAvailable'),
        color: 'orange-8',
        icon: 'mdi-new-box',
        position: 'bottom',
        timeout: 0,
        classes: 'rounded-borders-sharp q-px-md q-py-xs shadow-5',
        actions: [
          { label: t('versionCheck.close'), color: 'white', handler: () => {} },
          { label: t('versionCheck.reload'), color: 'primary', handler: reloadAndClearCache },
        ],
      })
    }
  } catch {
    // version.json not present on this environment — ignore
  }
}

onMounted(async () => {
  initUser()

  // Re-read the user from the server, then again whenever the app comes back to the
  // foreground. `Parse.User.current()` is a localStorage copy, so without this a field
  // flipped in the Parse dashboard — `demo_locked`, which unlocks the demo account for
  // maintenance — would not be seen until the next login. One tiny query, best-effort.
  void refreshUser().then(onRefreshed)
  document.addEventListener('visibilitychange', onForeground)

  // If this admin already opted into push, silently re-register so a rotated FCM
  // token is refreshed on the User. No-op on web / when permission isn't granted.
  // ⚠️ FIRST, and never behind an await that can throw: until 2026-09-15 this line sat
  // after `await checkForUpdates()`, which threw on every native launch ("AppUpdate"
  // plugin is not implemented) — so no staff device ever refreshed its FCM token and
  // booking pushes silently stopped once a token rotated.
  // The diner-only app (APP_MODE=guest) ships with no push and no Firebase at all.
  if (!IS_GUEST_APP) void refreshTokenIfEnabled()

  // Store-update check is best-effort: it must not be able to abort start-up.
  // `?appupdate=soft|force|ready` previews a card on any platform (see useAppUpdate).
  const updatePreview = /[?&]appupdate=(\w+)/.exec(window.location.href)?.[1]
  void checkForUpdates(updatePreview).catch((err) => console.warn('[appUpdate] check skipped:', err))

  await checkForNewVersion()
  versionCheckInterval = setInterval(() => {
    void checkForNewVersion()
  }, 300000)
})

function onForeground() {
  if (document.visibilityState === 'visible') void refreshUser().then(onRefreshed)
}

/**
 * The server no longer knows this session (logged out in another tab / device) — refreshUser
 * has already cleared it locally. Send the person to log in again, with a short note, rather
 * than leaving them on a page where every request now fails.
 */
function onRefreshed(result: 'ok' | 'expired' | 'skipped') {
  if (result !== 'expired') return
  if (IS_GUEST_APP) return   // diner-only app: no login to send anyone to
  if (router.currentRoute.value.path.startsWith('/login')) return
  void router.replace({ path: '/login', query: { expired: '1' } })
}

onUnmounted(() => {
  if (versionCheckInterval) clearInterval(versionCheckInterval)
  document.removeEventListener('visibilitychange', onForeground)
})
/*
onMounted(async () => {
  // ✅ Android App Update check
  if (Capacitor.getPlatform() === 'android') {
    try {
      await AppUpdate.checkForUpdate()
    } catch (err) {
      console.error('Update check failed:', err)
    }
  }

  // ✅ Hide preloader
  const preloader = document.getElementById('preloader')
  if (preloader) {
    await import('src/css/preload.css')
    preloader.remove()
  }

  setTimeout(async () => {
    await AppUpdate.checkForUpdate()
  }, 500)

  checkForUpdates()
})
*/

</script>
